import Alpine from 'alpinejs';
import { getCSRFToken } from '../utils/getCSRFToken';
import { formatDateISO, parseDate } from '../utils/dateUtils';

export default function registerCalendarSettingsDiscountsStore() {
  const existing = Alpine.store('calendarSettingsDiscountsStore');
  if (existing) return existing;

  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const store = {};
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(baseStore));

  const discountsExtensions = {
    activeDiscountTab: 'discounts',
    showCreateTaxFeeModal: false,
    showEditTaxFeeModal: false,
    newTaxFee: {
      name: '',
      amount: 0,
      is_tax: true,
      is_inclusive: true,
      applies_to_fees: false,
      type: 'PERCENTAGE',
      selected_ratesets: [],
    },
    editingTaxFee: {
      id: null,
      name: '',
      amount: 0,
      is_tax: true,
      is_inclusive: true,
      applies_to_fees: false,
      type: 'PERCENTAGE',
      selected_ratesets: [],
    },
    showCreateManualDiscountModal: false,
    showEditManualDiscountModal: false,
    showCreateDiscountModal: false,
    showEditDiscountModal: false,
    showDeleteDiscountConfirmation: false,
    discountsInitialized: false,
    discountsInitPromise: null,
    newDiscount: {
      name: '',
      discount_type: '',
      value_type: '',
      amount: 0,
      fixed_application_type: 'PER_DAY',
      min_days: null,
      max_days: null,
      days_before_arrival: null,
      min_people: null,
      max_people: null,
      active: true,
      start_date: null,
      end_date: null,
      is_recurring: false,
      selected_ratesets: [],
    },
    editingDiscount: {
      id: null,
      name: '',
      discount_type: '',
      value_type: '',
      amount: 0,
      fixed_application_type: 'PER_DAY',
      min_days: null,
      max_days: null,
      days_before_arrival: null,
      min_people: null,
      max_people: null,
      active: true,
      start_date: null,
      end_date: null,
      is_recurring: false,
      selected_ratesets: [],
    },
    discounts: [],
    createDiscountDateRangePicker: null,
    editDiscountDateRangePicker: null,
    editingManualDiscount: {
      id: null,
      name: '',
      value_type: 'PERCENTAGE',
      amount: '',
      active: true,
      partners: [],
      coupon_codes: [],
    },
    async init() {
      if (this.discountsInitialized) return;
      if (this.discountsInitPromise) return this.discountsInitPromise;

      this.discountsInitPromise = (async () => {
        this.initializeSettingsContext();
        await Promise.allSettled([
          this.loadTaxFees(),
          this.loadDiscounts(),
          this.loadPartners(),
        ]);
        window.setTimeout(() => this.ensureModalBindings(), 0);
        this.discountsInitialized = true;
      })();

      try {
        return await this.discountsInitPromise;
      } finally {
        this.discountsInitPromise = null;
      }
    },

    resetNewTaxFee() {
      this.newTaxFee = {
        name: '',
        amount: 0,
        is_tax: true,
        is_inclusive: true,
        applies_to_fees: false,
        type: 'PERCENTAGE',
        selected_ratesets: [],
      };
    },

    closeCreateTaxFeeModal() {
      this.showCreateTaxFeeModal = false;

      // Let Alpine finish modal teardown before resetting bound form state.
      window.setTimeout(() => {
        if (this.showCreateTaxFeeModal) return;
        this.resetNewTaxFee();
      }, 180);
    },

    async createTaxFee() {
      if (!this.calendarId) {
        console.error('Calendar ID not available');
        alert(
          'Calendar ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;

      try {
        const formData = new FormData();

        formData.append('calendar', this.calendarId);
        formData.append('name', this.newTaxFee.name);
        formData.append('amount', this.newTaxFee.amount);
        formData.append('is_tax', this.newTaxFee.is_tax);
        formData.append(
          'is_inclusive',
          this.newTaxFee.type === 'PERCENTAGE'
            ? this.newTaxFee.is_inclusive
            : false,
        );
        formData.append(
          'applies_to_fees',
          this.newTaxFee.is_tax ? this.newTaxFee.applies_to_fees : false,
        );
        formData.append('type', this.newTaxFee.type);
        formData.append(
          'selected_ratesets',
          JSON.stringify(this.newTaxFee.selected_ratesets || []),
        );

        const response = await fetch('/payments/create_tax_fee/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create tax/fee');
        }

        this.closeCreateTaxFeeModal();

        if (data.tax_fee) {
          this.tax_fees.push(data.tax_fee);
          if (data.tax_fee.is_tax) {
            this.availableTaxes.push(data.tax_fee);
          } else {
            this.availableFees.push(data.tax_fee);
          }
        }
      } catch (error) {
        console.error('Error creating tax/fee:', error);
        alert(error.message || 'An error occurred while creating the tax/fee');
      } finally {
        this.isLoading = false;
      }
    },

    openCreateTaxFeeModal() {
      this.showCreateTaxFeeModal = true;
    },

    editTaxFee(taxFeeId) {
      const numericId = parseInt(taxFeeId, 10);

      const taxFee = this.tax_fees.find(
        (tf) => parseInt(tf.id, 10) === numericId,
      );

      if (!taxFee) {
        console.error('Tax fee not found:', taxFeeId);
        return;
      }

      const selectedRatesets =
        taxFee.rateset_associations &&
        Array.isArray(taxFee.rateset_associations)
          ? [...taxFee.rateset_associations.map((id) => String(id))]
          : [];

      this.editingTaxFee = {
        id: taxFee.id,
        name: taxFee.name,
        amount: taxFee.amount,
        is_tax: taxFee.is_tax,
        is_inclusive: taxFee.is_inclusive,
        applies_to_fees: taxFee.applies_to_fees,
        type: taxFee.type,
        selected_ratesets: selectedRatesets,
      };

      this.showEditTaxFeeModal = true;
    },

    resetEditingTaxFee() {
      this.editingTaxFee = {
        id: null,
        name: '',
        amount: 0,
        is_tax: true,
        is_inclusive: true,
        applies_to_fees: false,
        type: 'PERCENTAGE',
        selected_ratesets: [],
      };
    },

    closeEditTaxFeeModal() {
      this.showEditTaxFeeModal = false;

      // Let Alpine finish modal teardown before resetting bound form state.
      window.setTimeout(() => {
        if (this.showEditTaxFeeModal) return;
        this.resetEditingTaxFee();
      }, 180);
    },

    async updateTaxFee() {
      if (!this.calendarId || !this.editingTaxFee.id) {
        console.error('Calendar ID or Tax Fee ID not available');
        alert(
          'Calendar ID or Tax Fee ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;

      try {
        const formData = new FormData();

        formData.append('tax_fee_id', this.editingTaxFee.id);
        formData.append('calendar', this.calendarId);
        formData.append('name', this.editingTaxFee.name);
        formData.append('amount', this.editingTaxFee.amount);
        formData.append('is_tax', this.editingTaxFee.is_tax);
        formData.append(
          'is_inclusive',
          this.editingTaxFee.type === 'PERCENTAGE'
            ? this.editingTaxFee.is_inclusive
            : false,
        );
        formData.append(
          'applies_to_fees',
          this.editingTaxFee.is_tax
            ? this.editingTaxFee.applies_to_fees
            : false,
        );
        formData.append('type', this.editingTaxFee.type);
        formData.append(
          'selected_ratesets',
          JSON.stringify(this.editingTaxFee.selected_ratesets || []),
        );

        const response = await fetch('/payments/update_tax_fee/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error || data.message || 'Failed to update tax/fee',
          );
        }

        this.closeEditTaxFeeModal();

        const taxFeeData = data.tax_fee;
        if (taxFeeData) {
          const taxFeeId = String(taxFeeData.id);
          const index = this.tax_fees.findIndex(
            (tf) => String(tf.id) === taxFeeId,
          );
          if (index !== -1) {
            const ratesetAssociations =
              taxFeeData.rateset_associations !== undefined
                ? taxFeeData.rateset_associations
                : this.tax_fees[index].rateset_associations || [];

            const updatedTaxFee = {
              ...this.tax_fees[index],
              ...taxFeeData,
              rateset_associations: ratesetAssociations,
            };

            this.tax_fees.splice(index, 1, updatedTaxFee);
            this.availableTaxes = this.tax_fees.filter((tf) => tf.is_tax);
            this.availableFees = this.tax_fees.filter((tf) => !tf.is_tax);
          } else {
            console.warn(
              'Tax/fee not found in list for update:',
              taxFeeData.id,
            );
          }
        } else {
          console.warn('No tax_fee in response:', data);
        }
      } catch (error) {
        console.error('Error updating tax/fee:', error);
        alert(error.message || 'An error occurred while updating the tax/fee');
      } finally {
        this.isLoading = false;
      }
    },

    async deleteTaxFee(taxFeeId) {
      if (!this.calendarId || !taxFeeId) {
        console.error('Calendar ID or Tax Fee ID not available');
        alert(
          'Calendar ID or Tax Fee ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;

      try {
        const response = await fetch('/payments/delete_tax_fee/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            tax_fee_id: taxFeeId,
            calendar: this.calendarId,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to delete tax/fee');
        }

        this.tax_fees = this.tax_fees.filter((tf) => tf.id !== taxFeeId);
        this.availableTaxes = this.tax_fees.filter((tf) => tf.is_tax);
        this.availableFees = this.tax_fees.filter((tf) => !tf.is_tax);
      } catch (error) {
        console.error('Error deleting tax/fee:', error);
        alert(error.message || 'An error occurred while deleting the tax/fee');
      } finally {
        this.isLoading = false;
      }
    },

    // Discount Management Methods
    loadDiscounts() {
      try {
        const discountsScript = document.getElementById('discounts-data');
        if (discountsScript) {
          const discountsData = discountsScript.textContent;
          this.discounts = JSON.parse(discountsData);
        } else {
          this.discounts = [];
        }
      } catch (error) {
        console.error('Error loading discounts:', error);
        this.discounts = [];
      }
    },

    async loadPartners() {
      try {
        if (!this.calendarId) {
          this.partners = [];
          return;
        }

        const response = await fetch(
          `/partners/api/partners/?calendar_id=${this.calendarId}`,
        );
        if (!response.ok) {
          this.partners = [];
          return;
        }

        const partners = await response.json();
        this.partners = Array.isArray(partners) ? partners : [];
      } catch (error) {
        console.error('Error loading partners for discounts:', error);
        this.partners = [];
      }
    },

    toggleWeekday(weekdayIndex, discountType) {
      const weekday = Number(weekdayIndex);
      const discount =
        discountType === 'newDiscount'
          ? this.newDiscount
          : this.editingDiscount;

      if (!discount.weekdays) {
        discount.weekdays = [];
      }

      const index = discount.weekdays.indexOf(weekday);
      if (index > -1) {
        discount.weekdays.splice(index, 1);
      } else {
        discount.weekdays.push(weekday);
      }

      discount.weekdays.sort((a, b) => a - b);
    },

    resetNewDiscount() {
      this.newDiscount = {
        name: '',
        discount_type: '',
        value_type: '',
        amount: 0,
        fixed_application_type: 'PER_DAY',
        min_days: null,
        max_days: null,
        days_before_arrival: null,
        min_people: null,
        max_people: null,
        weekdays: [],
        active: true,
        start_date: null,
        end_date: null,
        is_recurring: false,
        selected_ratesets: [],
      };
      this.clearCreateDiscountDateRangePicker();
    },

    closeCreateDiscountModal() {
      this.showCreateDiscountModal = false;

      // Let Alpine finish modal teardown before resetting bound form state.
      window.setTimeout(() => {
        if (this.showCreateDiscountModal) return;
        this.resetNewDiscount();
      }, 180);
    },

    resetEditingDiscount() {
      this.editingDiscount = {
        id: null,
        name: '',
        discount_type: '',
        value_type: '',
        amount: 0,
        fixed_application_type: 'PER_DAY',
        min_days: null,
        max_days: null,
        days_before_arrival: null,
        min_people: null,
        max_people: null,
        active: true,
        start_date: null,
        end_date: null,
        is_recurring: false,
        selected_ratesets: [],
      };
      this.showDeleteDiscountConfirmation = false;
      this.clearEditDiscountDateRangePicker();
    },

    closeEditDiscountModal() {
      this.showEditDiscountModal = false;

      // Let Alpine finish modal teardown before resetting bound form state.
      window.setTimeout(() => {
        if (this.showEditDiscountModal) return;
        this.resetEditingDiscount();
      }, 180);
    },

    isPeriodLongerThanYear(startDate, endDate) {
      if (!startDate || !endDate) return false;
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      if (!start || !end) return false;
      const oneYear = 365 * 24 * 60 * 60 * 1000;
      return end - start > oneYear;
    },

    getDatePeriodDescription(startDate, endDate, isRecurring) {
      if (!startDate || !endDate) return 'Always active';

      const start = parseDate(startDate);
      const end = parseDate(endDate);
      if (!start || !end) return 'Always active';

      if (this.isPeriodLongerThanYear(startDate, endDate)) {
        return isRecurring
          ? 'Always active (period > 1 year)'
          : `${start.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })} - ${end.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })} (one-time)`;
      }

      if (isRecurring) {
        const startStr = start.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        const endStr = end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        return `${startStr} - ${endStr} (recurring yearly)`;
      }

      const startStr = start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      const endStr = end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${startStr} - ${endStr} (one-time)`;
    },

    getWeekdaysDisplay(weekdays) {
      if (!weekdays || weekdays.length === 0) return '';
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const selectedDays = weekdays
        .map((w) => Number(w))
        .sort((a, b) => a - b)
        .map((w) => dayNames[w]);

      if (selectedDays.length === 7) return 'All days';
      if (this.isConsecutiveDays(weekdays)) {
        return `${selectedDays[0]}-${selectedDays[selectedDays.length - 1]}`;
      }
      return selectedDays.join(', ');
    },

    isConsecutiveDays(weekdays) {
      if (!weekdays || weekdays.length < 2) return false;
      const sorted = weekdays.map((w) => Number(w)).sort((a, b) => a - b);
      for (let i = 1; i < sorted.length; i++) {
        const diff = sorted[i] - sorted[i - 1];
        if (diff !== 1 && !(sorted[i] === 0 && sorted[i - 1] === 6)) {
          return false;
        }
      }
      return true;
    },

    initializeCreateDiscountDateRangePicker(element) {
      if (typeof flatpickr === 'undefined') {
        return;
      }

      const store = this;
      store.createDiscountDateRangePicker = flatpickr(element, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd-m-Y',
        showMonths: 2,
        allowInput: false,
        onChange: function (selectedDates) {
          if (selectedDates.length === 2) {
            const startDate = formatDateISO(selectedDates[0]);
            const endDate = formatDateISO(selectedDates[1]);
            store.newDiscount.start_date = startDate;
            store.newDiscount.end_date = endDate;
          } else if (selectedDates.length === 1) {
            const startDate = formatDateISO(selectedDates[0]);
            store.newDiscount.start_date = startDate;
            store.newDiscount.end_date = null;
          } else {
            store.newDiscount.start_date = null;
            store.newDiscount.end_date = null;
          }
        },
      });
    },

    initializeEditDiscountDateRangePicker(element) {
      if (typeof flatpickr === 'undefined') {
        return;
      }

      const store = this;
      store.editDiscountDateRangePicker = flatpickr(element, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd-m-Y',
        showMonths: 2,
        allowInput: false,
        onChange: function (selectedDates) {
          if (selectedDates.length === 2) {
            const startDate = formatDateISO(selectedDates[0]);
            const endDate = formatDateISO(selectedDates[1]);
            store.editingDiscount.start_date = startDate;
            store.editingDiscount.end_date = endDate;
          } else if (selectedDates.length === 1) {
            const startDate = formatDateISO(selectedDates[0]);
            store.editingDiscount.start_date = startDate;
            store.editingDiscount.end_date = null;
          } else {
            store.editingDiscount.start_date = null;
            store.editingDiscount.end_date = null;
          }
        },
      });
    },

    clearCreateDiscountDateRangePicker() {
      if (this.createDiscountDateRangePicker) {
        this.createDiscountDateRangePicker.clear();
      }
    },

    clearEditDiscountDateRangePicker() {
      if (this.editDiscountDateRangePicker) {
        this.editDiscountDateRangePicker.clear();
      }
    },

    validateDatePeriod(startDate, endDate) {
      const errors = [];
      if ((startDate && !endDate) || (!startDate && endDate)) {
        errors.push(
          'Both start date and end date must be provided together, or leave both empty for always active.',
        );
      }

      if (startDate && endDate) {
        const start = parseDate(startDate);
        const end = parseDate(endDate);
        if (!start || !end) {
          errors.push('Invalid date period.');
          return errors;
        }
        if (start >= end) {
          errors.push('End date must be after start date.');
        }

        const now = new Date();
        const fiveYearsAgo = new Date(
          now.getFullYear() - 5,
          now.getMonth(),
          now.getDate(),
        );
        const tenYearsFromNow = new Date(
          now.getFullYear() + 10,
          now.getMonth(),
          now.getDate(),
        );

        if (start < fiveYearsAgo) {
          errors.push(
            'Start date should not be more than 5 years in the past.',
          );
        }
        if (end > tenYearsFromNow) {
          errors.push(
            'End date should not be more than 10 years in the future.',
          );
        }
      }

      return errors;
    },

    validateDiscountForm(discount) {
      const errors = [];
      if (!discount.name || discount.name.trim().length === 0) {
        errors.push('Discount name is required.');
      }
      if (!discount.discount_type) {
        errors.push('Discount type is required.');
      }
      if (!discount.value_type) {
        errors.push('Value type is required.');
      }
      if (!discount.amount || discount.amount <= 0) {
        errors.push('Amount must be greater than 0.');
      }
      if (discount.value_type === 'PERCENTAGE' && discount.amount > 100) {
        errors.push('Percentage cannot exceed 100%.');
      }

      errors.push(
        ...this.validateDatePeriod(discount.start_date, discount.end_date),
      );

      if (discount.discount_type === 'LENGTH_OF_STAY') {
        if (
          discount.min_days &&
          discount.max_days &&
          discount.min_days > discount.max_days
        ) {
          errors.push('Minimum days cannot be greater than maximum days.');
        }
      }

      if (['EARLY_BIRD', 'LAST_MINUTE'].includes(discount.discount_type)) {
        if (!discount.days_before_arrival || discount.days_before_arrival < 0) {
          errors.push('Days before arrival must be 0 or greater.');
        }
      }

      if (discount.discount_type === 'WEEKDAY') {
        if (!discount.weekdays || discount.weekdays.length === 0) {
          errors.push('At least one day of the week must be selected.');
        }
      }

      if (discount.discount_type === 'AMOUNT_PEOPLE') {
        if (!discount.min_people || discount.min_people < 1) {
          errors.push('From people must be 1 or greater.');
        }
        if (
          discount.min_people &&
          discount.max_people &&
          Number(discount.max_people) <= Number(discount.min_people)
        ) {
          errors.push('To people must be greater than from people.');
        }
      }

      return errors;
    },

    async createDiscount() {
      if (this.isLoading) return;

      if (!this.calendarId) {
        alert(
          'Calendar ID not available. Please refresh the page and try again.',
        );
        return;
      }

      const validationErrors = this.validateDiscountForm(this.newDiscount);
      if (validationErrors.length > 0) {
        alert(
          `Please fix the following errors:\n\n${validationErrors.join('\n')}`,
        );
        return;
      }

      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('calendar', this.calendarId);
        formData.append('name', this.newDiscount.name);
        formData.append('discount_type', this.newDiscount.discount_type);
        formData.append('value_type', this.newDiscount.value_type);
        formData.append('amount', this.newDiscount.amount);
        formData.append('active', this.newDiscount.active);

        if (this.newDiscount.start_date) {
          formData.append('start_date', this.newDiscount.start_date);
        }
        if (this.newDiscount.end_date) {
          formData.append('end_date', this.newDiscount.end_date);
        }
        formData.append('is_recurring', this.newDiscount.is_recurring);

        if (this.newDiscount.value_type === 'FIXED') {
          formData.append(
            'fixed_application_type',
            this.newDiscount.fixed_application_type,
          );
        }

        if (this.newDiscount.discount_type === 'LENGTH_OF_STAY') {
          if (this.newDiscount.min_days)
            formData.append('min_days', this.newDiscount.min_days);
          if (this.newDiscount.max_days)
            formData.append('max_days', this.newDiscount.max_days);
        } else if (
          ['EARLY_BIRD', 'LAST_MINUTE'].includes(this.newDiscount.discount_type)
        ) {
          if (this.newDiscount.days_before_arrival) {
            formData.append(
              'days_before_arrival',
              this.newDiscount.days_before_arrival,
            );
          }
        } else if (this.newDiscount.discount_type === 'AMOUNT_PEOPLE') {
          if (this.newDiscount.min_people)
            formData.append('min_people', this.newDiscount.min_people);
          if (this.newDiscount.max_people)
            formData.append('max_people', this.newDiscount.max_people);
        } else if (this.newDiscount.discount_type === 'WEEKDAY') {
          if (
            this.newDiscount.weekdays &&
            this.newDiscount.weekdays.length > 0
          ) {
            formData.append(
              'weekdays',
              JSON.stringify(this.newDiscount.weekdays),
            );
          }
        }

        formData.append(
          'selected_ratesets',
          JSON.stringify(this.newDiscount.selected_ratesets || []),
        );

        const response = await fetch('/rates/create_discount/', {
          method: 'POST',
          headers: { 'X-CSRFToken': getCSRFToken() },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to create discount');
        }

        this.closeCreateDiscountModal();
        if (data.discount) {
          this.discounts.push(data.discount);
        }
      } catch (error) {
        console.error('Error creating discount:', error);
        alert(error.message || 'An error occurred while creating the discount');
      } finally {
        this.isLoading = false;
      }
    },

    editDiscount(discountId) {
      const stringId = String(discountId);
      const discount = this.discounts.find((d) => String(d.id) === stringId);
      if (!discount) return;

      if (discount.application_mode === 'MANUAL') {
        if (typeof this.editManualDiscount === 'function') {
          this.editManualDiscount(discountId);
        }
        return;
      }

      const weekdays = (discount.weekdays || []).map((w) => Number(w));
      this.editingDiscount = {
        id: discount.id,
        name: discount.name,
        discount_type: discount.discount_type,
        value_type: discount.value_type,
        amount: discount.amount,
        fixed_application_type: discount.fixed_application_type || 'PER_DAY',
        min_days: discount.min_days,
        max_days: discount.max_days,
        days_before_arrival: discount.days_before_arrival,
        min_people: discount.min_people,
        max_people: discount.max_people,
        weekdays,
        active: discount.active,
        start_date: discount.start_date || null,
        end_date: discount.end_date || null,
        is_recurring: discount.is_recurring || false,
        selected_ratesets: Array.isArray(discount.rateset_associations)
          ? [...discount.rateset_associations.map((id) => String(id))]
          : [],
      };

      this.showEditDiscountModal = true;
      if (
        this.editDiscountDateRangePicker &&
        discount.start_date &&
        discount.end_date
      ) {
        setTimeout(() => {
          this.editDiscountDateRangePicker.setDate([
            discount.start_date,
            discount.end_date,
          ]);
        }, 100);
      }
    },

    async updateDiscount() {
      if (!this.calendarId || !this.editingDiscount.id) {
        alert(
          'Calendar ID or Discount ID not available. Please refresh the page and try again.',
        );
        return;
      }

      const validationErrors = this.validateDiscountForm(this.editingDiscount);
      if (validationErrors.length > 0) {
        alert(
          `Please fix the following errors:\n\n${validationErrors.join('\n')}`,
        );
        return;
      }

      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('discount_id', this.editingDiscount.id);
        formData.append('calendar', this.calendarId);
        formData.append('name', this.editingDiscount.name);
        formData.append('discount_type', this.editingDiscount.discount_type);
        formData.append('value_type', this.editingDiscount.value_type);
        formData.append('amount', this.editingDiscount.amount);
        formData.append('active', this.editingDiscount.active);

        if (this.editingDiscount.start_date) {
          formData.append('start_date', this.editingDiscount.start_date);
        }
        if (this.editingDiscount.end_date) {
          formData.append('end_date', this.editingDiscount.end_date);
        }
        formData.append('is_recurring', this.editingDiscount.is_recurring);

        if (this.editingDiscount.value_type === 'FIXED') {
          formData.append(
            'fixed_application_type',
            this.editingDiscount.fixed_application_type,
          );
        }

        if (this.editingDiscount.discount_type === 'LENGTH_OF_STAY') {
          if (this.editingDiscount.min_days) {
            formData.append('min_days', this.editingDiscount.min_days);
          }
          if (this.editingDiscount.max_days) {
            formData.append('max_days', this.editingDiscount.max_days);
          }
        } else if (
          ['EARLY_BIRD', 'LAST_MINUTE'].includes(
            this.editingDiscount.discount_type,
          )
        ) {
          if (this.editingDiscount.days_before_arrival) {
            formData.append(
              'days_before_arrival',
              this.editingDiscount.days_before_arrival,
            );
          }
        } else if (this.editingDiscount.discount_type === 'AMOUNT_PEOPLE') {
          if (this.editingDiscount.min_people) {
            formData.append('min_people', this.editingDiscount.min_people);
          }
          if (this.editingDiscount.max_people) {
            formData.append('max_people', this.editingDiscount.max_people);
          }
        } else if (this.editingDiscount.discount_type === 'WEEKDAY') {
          if (
            this.editingDiscount.weekdays &&
            this.editingDiscount.weekdays.length > 0
          ) {
            formData.append(
              'weekdays',
              JSON.stringify(this.editingDiscount.weekdays),
            );
          }
        }

        formData.append(
          'selected_ratesets',
          JSON.stringify(this.editingDiscount.selected_ratesets || []),
        );

        const response = await fetch('/rates/update_discount/', {
          method: 'POST',
          headers: { 'X-CSRFToken': getCSRFToken() },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to update discount');
        }

        this.closeEditDiscountModal();
        if (data.discount) {
          const discountId = String(data.discount.id);
          const index = this.discounts.findIndex(
            (d) => String(d.id) === discountId,
          );
          if (index !== -1) {
            this.discounts.splice(index, 1, data.discount);
          }
        }
      } catch (error) {
        console.error('Error updating discount:', error);
        alert(error.message || 'An error occurred while updating the discount');
      } finally {
        this.isLoading = false;
      }
    },

    async deleteDiscount() {
      if (!this.calendarId || !this.editingDiscount.id) {
        alert(
          'Calendar ID or Discount ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;
      try {
        const response = await fetch('/rates/delete_discount/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            discount_id: this.editingDiscount.id,
            calendar: this.calendarId,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to delete discount');
        }

        this.closeEditDiscountModal();
        this.showDeleteDiscountConfirmation = false;
        const discountId = this.editingDiscount.id;
        this.discounts = this.discounts.filter((d) => d.id !== discountId);
      } catch (error) {
        console.error('Error deleting discount:', error);
        alert(error.message || 'An error occurred while deleting the discount');
      } finally {
        this.isLoading = false;
      }
    },

    async createManualDiscount(discountData) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('calendar', this.calendarId);
        formData.append('name', discountData.name);
        formData.append('value_type', discountData.value_type);
        formData.append('amount', discountData.amount);
        formData.append('application_mode', 'MANUAL');
        formData.append('discount_type', '');

        const response = await fetch('/rates/create_discount/', {
          method: 'POST',
          headers: { 'X-CSRFToken': getCSRFToken() },
          body: formData,
        });

        const data = await response.json();
        if (response.ok) {
          if (data.discount) {
            this.discounts.push(data.discount);
          }
          this.closeCreateManualDiscountModal();
          this._toast('Manual discount created', 'success');
        } else {
          this._toast(
            data.error || 'Failed to create manual discount',
            'danger',
          );
        }
      } catch (error) {
        console.error('Error creating manual discount:', error);
        this._toast('Failed to create manual discount', 'danger');
      } finally {
        this.isLoading = false;
      }
    },

    async editManualDiscount(discountId) {
      const stringId = String(discountId);
      const discount = this.discounts.find((d) => String(d.id) === stringId);
      if (!discount) {
        console.error('Manual discount not found:', discountId);
        return;
      }

      if (!Array.isArray(this.partners) || this.partners.length === 0) {
        await this.loadPartners();
      }

      const partners = Array.isArray(discount.partners)
        ? discount.partners.map((p) => ({
            id: String(p.id),
            name: p.name || '',
          }))
        : discount.partner_id
        ? [
            {
              id: String(discount.partner_id),
              name: discount.partner_name || '',
            },
          ]
        : [];

      this.editingManualDiscount = {
        id: discount.id,
        name: discount.name,
        value_type: discount.value_type,
        amount: discount.amount,
        active: discount.active,
        partners,
        coupon_codes: discount.coupon_codes ? [...discount.coupon_codes] : [],
      };

      this.showEditManualDiscountModal = true;
    },

    openCreateManualDiscountModal() {
      this.showCreateManualDiscountModal = true;
    },

    closeCreateManualDiscountModal() {
      this.showCreateManualDiscountModal = false;
    },

    resetEditingManualDiscount() {
      this.editingManualDiscount = {
        id: null,
        name: '',
        value_type: 'PERCENTAGE',
        amount: '',
        active: true,
        partners: [],
        coupon_codes: [],
      };
    },

    closeEditManualDiscountModal() {
      this.showEditManualDiscountModal = false;

      // Let Alpine finish modal teardown before resetting bound form state.
      window.setTimeout(() => {
        if (this.showEditManualDiscountModal) return;
        this.resetEditingManualDiscount();
      }, 180);
    },

    addPartnerToManualDiscount(partnerId) {
      if (!partnerId) return;
      const partner = this.partners.find(
        (p) => String(p.id) === String(partnerId),
      );
      if (!partner) return;
      const already = this.editingManualDiscount.partners.some(
        (p) => String(p.id) === String(partnerId),
      );
      if (already) return;
      this.editingManualDiscount.partners = [
        ...this.editingManualDiscount.partners,
        { id: String(partner.id), name: partner.name },
      ];
    },

    removePartnerFromManualDiscount(partnerId) {
      this.editingManualDiscount.partners =
        this.editingManualDiscount.partners.filter(
          (p) => String(p.id) !== String(partnerId),
        );
    },

    async updateManualDiscount() {
      if (!this.calendarId || !this.editingManualDiscount.id) {
        alert('Calendar ID or Discount ID not available.');
        return;
      }

      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const formData = new FormData();
        formData.append('discount_id', this.editingManualDiscount.id);
        formData.append('calendar', this.calendarId);
        formData.append('name', this.editingManualDiscount.name);
        formData.append('value_type', this.editingManualDiscount.value_type);
        formData.append('amount', this.editingManualDiscount.amount);
        formData.append('active', this.editingManualDiscount.active);
        formData.append('application_mode', 'MANUAL');
        formData.append('discount_type', '');
        const partnerIds = (this.editingManualDiscount.partners || []).map(
          (p) => p.id,
        );
        formData.append('partner_ids', JSON.stringify(partnerIds));

        const response = await fetch('/rates/update_discount/', {
          method: 'POST',
          headers: { 'X-CSRFToken': getCSRFToken() },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to update discount');
        }

        if (data.discount) {
          const index = this.discounts.findIndex(
            (d) => String(d.id) === String(data.discount.id),
          );
          if (index !== -1) {
            this.discounts.splice(index, 1, data.discount);
          }
        }

        this.closeEditManualDiscountModal();
        this._toast('Manual discount updated', 'success');
      } catch (error) {
        console.error('Error updating manual discount:', error);
        this._toast(
          error.message || 'Failed to update manual discount',
          'danger',
        );
      } finally {
        this.isLoading = false;
      }
    },

    async deleteManualDiscount() {
      if (!this.calendarId || !this.editingManualDiscount.id) return;

      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const response = await fetch('/rates/delete_discount/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            discount_id: this.editingManualDiscount.id,
            calendar: this.calendarId,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          this.discounts = this.discounts.filter(
            (d) => String(d.id) !== String(this.editingManualDiscount.id),
          );
          this.closeEditManualDiscountModal();
          this._toast('Manual discount deleted', 'success');
        } else {
          this._toast(data.error || 'Failed to delete discount', 'danger');
        }
      } catch (error) {
        console.error('Error deleting manual discount:', error);
        this._toast('Failed to delete manual discount', 'danger');
      } finally {
        this.isLoading = false;
      }
    },

    async createCouponCode(code, usageLimit, startDate, endDate) {
      if (!this.editingManualDiscount.id) return;
      try {
        const response = await fetch('/rates/coupon-codes/create/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            discount_id: this.editingManualDiscount.id,
            code: code,
            usage_limit: usageLimit ? parseInt(usageLimit, 10) : null,
            start_date: startDate || null,
            end_date: endDate || null,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          this.editingManualDiscount.coupon_codes.push(data.coupon);
          this._syncCouponsToMainList();
          this._toast('Coupon code created', 'success');
        } else {
          this._toast(data.error || 'Failed to create coupon code', 'danger');
        }
      } catch (error) {
        console.error('Error creating coupon code:', error);
        this._toast('Failed to create coupon code', 'danger');
      }
    },

    async toggleCouponCode(couponId) {
      try {
        const response = await fetch(
          `/rates/coupon-codes/${couponId}/toggle/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        const data = await response.json();
        if (response.ok) {
          const coupon = this.editingManualDiscount.coupon_codes.find(
            (c) => String(c.id) === String(couponId),
          );
          if (coupon) coupon.is_active = data.is_active;
          this._syncCouponsToMainList();
          this._toast(
            data.is_active ? 'Coupon activated' : 'Coupon deactivated',
            'success',
          );
        }
      } catch (error) {
        console.error('Error toggling coupon code:', error);
        this._toast('Failed to toggle coupon code', 'danger');
      }
    },

    async deleteCouponCode(couponId) {
      if (!confirm('Are you sure you want to delete this coupon code?')) return;
      try {
        const response = await fetch(
          `/rates/coupon-codes/${couponId}/delete/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        await response.json();
        if (response.ok) {
          this.editingManualDiscount.coupon_codes =
            this.editingManualDiscount.coupon_codes.filter(
              (c) => String(c.id) !== String(couponId),
            );
          this._syncCouponsToMainList();
          this._toast('Coupon code deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting coupon code:', error);
        this._toast('Failed to delete coupon code', 'danger');
      }
    },

    _syncCouponsToMainList() {
      const discountId = String(this.editingManualDiscount.id);
      const index = this.discounts.findIndex(
        (d) => String(d.id) === discountId,
      );
      if (index !== -1) {
        this.discounts[index].coupon_codes = [
          ...this.editingManualDiscount.coupon_codes,
        ];
      }
    },
  };

  Object.defineProperties(
    store,
    Object.getOwnPropertyDescriptors(discountsExtensions),
  );

  return Alpine.store('calendarSettingsDiscountsStore', store);
}
